import { logger } from '@sentry/react';

/**
 * Serializes data to a base64-encoded JSON string
 *
 * @template T - The type of data being serialized
 * @param data - The data to serialize
 * @returns Base64-encoded JSON string
 */
export function serializeBase64Json<T>(data: T): string {
    return btoa(JSON.stringify(data));
}

/**
 * Deserializes a base64-encoded JSON string with optional post-processing.
 * Handles errors gracefully by returning a default value on failure.
 *
 * @template T - The expected type of the deserialized data
 * @param serialized - Base64-encoded JSON string or null
 * @param defaultValue - Default value to return if deserialization fails
 * @param postProcess - Optional function to transform the parsed data
 * @returns Parsed data, processed if provided, or default value
 */
export function deserializeBase64Json<T>(
    serialized: string | null,
    defaultValue: T,
    postProcess?: (data: unknown) => unknown,
): T {
    if (!serialized) {
        return defaultValue;
    }
    try {
        const parsed = JSON.parse(atob(serialized));
        return (postProcess?.(parsed) ?? parsed) as T;
    } catch (error) {
        logger.warn('Failed to deserialize URL parameter', {
            error,
            serialized,
        });
        return defaultValue;
    }
}
